import * as yup from "yup";
import {
  DEFAULT_RESOURCE_LANGUAGE,
  EducationalResourceStatus,
  EducationalResourceType,
  RESOURCE_LANGUAGES,
  RESOURCE_STATUSES,
  RESOURCE_TYPES,
  ResourceLanguageCode,
  hasDuration,
} from "@/domain/educationalResource.interface";

/** PRET-2045 form rules, mirroring the API (BR-04..BR-10). */

export const REQUIRED_MESSAGE = "This field is required";
export const HTTPS_MESSAGE = "Enter a valid https URL";
export const MAX_DURATION_SECONDS = 86400;

export const isHttpsUrl = (value: string | undefined | null): boolean => {
  if (!value) return false;
  if (/\s/.test(value)) return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && Boolean(url.hostname);
  } catch {
    return false;
  }
};

const languageSchema = yup.object({
  language: yup
    .mixed<ResourceLanguageCode>()
    .oneOf(RESOURCE_LANGUAGES, REQUIRED_MESSAGE)
    .required(REQUIRED_MESSAGE),
  title: yup
    .string()
    .trim()
    .required("Title is required")
    .max(200, "Max 200 characters"),
  description: yup.string().trim().max(1000, "Max 1000 characters").default(""),
  url: yup
    .string()
    .trim()
    .required("URL is required")
    .test("https", HTTPS_MESSAGE, (v) => isHttpsUrl(v))
    .max(500, "Max 500 characters"),
});

export const educationalResourceSchema = yup.object({
  categoryId: yup.string().trim().required(REQUIRED_MESSAGE),
  type: yup
    .mixed<EducationalResourceType>()
    .oneOf(RESOURCE_TYPES, REQUIRED_MESSAGE)
    .required(REQUIRED_MESSAGE),
  status: yup
    .mixed<EducationalResourceStatus>()
    .oneOf(RESOURCE_STATUSES, REQUIRED_MESSAGE)
    .required(REQUIRED_MESSAGE),
  thumbnailUrl: yup
    .string()
    .trim()
    .default("")
    .test("https", HTTPS_MESSAGE, (v) => !v || isHttpsUrl(v))
    .max(500, "Max 500 characters"),
  duration: yup
    .string()
    .trim()
    .default("")
    .test("duration", function (value) {
      // Only VIDEO/AUDIO carry a duration; for any other type the value is dropped, not validated.
      if (!value || !hasDuration(this.parent?.type)) return true;
      const n = Number(value);
      if (Number.isNaN(n)) {
        return this.createError({ message: "Must be a whole number" });
      }
      if (n < 0) return this.createError({ message: "Must be 0 or more" });
      if (!Number.isInteger(n)) {
        return this.createError({ message: "Must be a whole number" });
      }
      if (n > MAX_DURATION_SECONDS) {
        return this.createError({ message: "Max 86400 seconds" });
      }
      return true;
    }),
  languages: yup
    .array()
    .of(languageSchema)
    .required()
    .min(1, "At least one language is required")
    .max(3, "At most 3 languages")
    .test(
      "unique-languages",
      "Each language can be used only once",
      (langs) => {
        const codes = (langs || []).map((l) => l?.language);
        return new Set(codes).size === codes.length;
      }
    )
    .test("has-default-language", "English (US) is required", (langs) =>
      (langs || []).some((l) => l?.language === DEFAULT_RESOURCE_LANGUAGE)
    ),
});
